import React, { useEffect } from "react"
import { Flex, Form, InputNumber, Switch, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useWatch } from "antd/es/form/Form"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import styles from "./AvailabilityDisplay.module.css"

export const AvailabilityDisplay = () => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const isEnabled = useWatch("low_availability_enabled")
  // time_slots has no Form.Item (TicketedSchedule writes it via setFieldValue),
  // so the watch needs preserve — otherwise it skips the unregistered field and stays undefined
  const timeSlots = useWatch("time_slots", { form, preserve: true })
  const slotCount = timeSlots?.length ?? 0

  // Re-check the cap when the schedule shrinks after the threshold was set
  // (e.g. user deletes start times on this same tab).
  useEffect(() => {
    if (isEnabled && form.getFieldValue("low_availability_threshold") != null) {
      form.validateFields(["low_availability_threshold"]).catch(() => {})
    }
  }, [slotCount, isEnabled, form])

  return (
    <Card title={t("experiences.ticketed_schedule.availability_title")} className="mb-3xl">
      <Flex vertical gap={16}>
        <Flex align="center" gap={8}>
          <Form.Item name="low_availability_enabled" valuePropName="checked" initialValue={true} noStyle>
            <Switch />
          </Form.Item>
          <Text>{t("experiences.ticketed_schedule.low_availability_label")}</Text>
          <Tooltip title={t("experiences.ticketed_schedule.low_availability_tooltip")}>
            <InfoCircleOutlined className={styles.infoIcon} />
          </Tooltip>
        </Flex>

        {isEnabled && (
          <Flex align="flex-start" gap={8}>
            <Text className={styles.thresholdLabel}>{t("experiences.ticketed_schedule.threshold_label")}</Text>
            <Form.Item
              name="low_availability_threshold"
              initialValue={1}
              style={{ marginBottom: 0 }}
              rules={[
                { required: true, message: t("experiences.ticketed_schedule.threshold_required") },
                { type: "number", min: 1, message: t("experiences.ticketed_schedule.threshold_min") },
                {
                  validator: (_, value) => {
                    if (value != null && slotCount > 0 && value > slotCount) {
                      return Promise.reject(t("experiences.ticketed_schedule.threshold_exceeds_slots", { count: slotCount }))
                    }
                    return Promise.resolve()
                  }
                }
              ]}
            >
              {/* No min={1} — antd would clamp a typed 0 to 1 on blur; the rule above shows an error instead */}
              <InputNumber min={0} className={styles.thresholdInput} size="middle" />
            </Form.Item>
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
